import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';

interface TourRequestFormProps {
  propertyId: string;
  agentId: string;
  propertyTitle: string;
}

const TourRequestForm: React.FC<TourRequestFormProps> = ({ propertyId: _propertyId, agentId: _agentId, propertyTitle }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    toast({
      title: t('tour.success'),
      description: t('tour.successDescription'),
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      preferredDate: '',
      preferredTime: '',
      message: ''
    });

    setIsSubmitting(false);
  };

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('tour.schedule')}</h3>
      <p className="text-sm text-gray-600 mb-6">
        {t('tour.requestShowing')} {propertyTitle}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <Label htmlFor="name">{t('tour.fullName')} *</Label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder={t('contact.namePlaceholder')}
            className="mt-1"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">{t('tour.emailRequired')} *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder={t('contact.emailPlaceholder')}
            className="mt-1"
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone">{t('tour.phoneRequired')} *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder={t('contact.phonePlaceholder')}
            className="mt-1"
          />
        </div>

        {/* Preferred Date */}
        <div>
          <Label htmlFor="preferredDate" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {t('tour.date')} *
          </Label>
          <Input
            id="preferredDate"
            name="preferredDate"
            type="date"
            value={formData.preferredDate}
            onChange={handleChange}
            required
            min={new Date().toISOString().split('T')[0]}
            className="mt-1"
          />
        </div>

        {/* Preferred Time */}
        <div>
          <Label htmlFor="preferredTime" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t('tour.time')} *
          </Label>
          <select
            id="preferredTime"
            name="preferredTime"
            value={formData.preferredTime}
            onChange={(e) => setFormData(prev => ({ ...prev, preferredTime: e.target.value }))}
            required
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('tour.selectTime')}</option>
            {timeSlots.map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <Label htmlFor="message">{t('tour.additionalMessage')}</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={t('tour.messagePlaceholder')}
            rows={3}
            className="mt-1"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? (
            t('tour.sending')
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              {t('tour.request')}
            </>
          )}
        </Button>
      </form>

      <p className="text-xs text-gray-500 mt-4">
        {t('tour.disclaimer')}
      </p>
    </div>
  );
};

export default TourRequestForm;
